import * as React from 'react';

import { Select, TextField, NumberField, TextArea } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';
import {
  getEntities,
  getEventCasesForEntity,
} from '../../editor/nodes/triggers/BusinessEventTrigger/schema';

export type BusinessEventTestPayload = {
  entityType: string;
  eventCase: string;
  data: Record<string, unknown>;
};

type BusinessEventTestFormProps = {
  value: BusinessEventTestPayload;
  onChange: (value: BusinessEventTestPayload) => void;
  configuredEntityType?: string;
  configuredEventCase?: string;
};

const BOOKING_STATUSES = ['DRAFT', 'OPEN', 'SHIPPED', 'COMPLETED', 'VOIDED'];
const LOAD_STATUSES = [
  'PENDING',
  'RECONCILED',
  'SHIPPED',
  'RECEIVED',
  'UNASSIGNED',
  'VOIDED',
];
const SALES_ORDER_STATUSES = ['DRAFT', 'OPEN', 'FINISHED', 'VOIDED'];
const SALES_ORDER_TYPES = ['DOMESTIC', 'INTERNATIONAL'];
const INVOICE_STATUSES = ['PENDING', 'PAID', 'VOIDED'];
const INVOICE_TYPES = ['INBOUND', 'OUTBOUND', 'FREIGHT', 'ACCRUAL'];

const BookingFields: React.FC<{
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}> = ({ data, onChange }) => {
  const t = useTranslations();
  const handleChange = (field: string, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="@BookingFields space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.bookingId}
          value={(data.id as string) || ''}
          onChange={(v) => handleChange('id', v)}
          placeholder="bkng_test123"
        />
        <TextField
          label={t.testForm.bookingNumber}
          value={(data.bookingNumber as string) || ''}
          onChange={(v) => handleChange('bookingNumber', v)}
          placeholder="BK-2024-001"
        />
      </div>
      <Select<string>
        label={t.form.status}
        value={(data.status as string) || 'DRAFT'}
        onChange={(v: string) => handleChange('status', v)}
        options={BOOKING_STATUSES.map((s) => ({ key: s, value: s, label: s }))}
      />
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.vesselName}
          value={(data.vesselName as string) || ''}
          onChange={(v) => handleChange('vesselName', v)}
          placeholder="MSC Oscar"
        />
        <TextField
          label={t.testForm.steamshipLine}
          value={(data.steamshipLine as string) || ''}
          onChange={(v) => handleChange('steamshipLine', v)}
          placeholder="MSC"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.loadingPortId}
          value={(data.loadingPortId as string) || ''}
          onChange={(v) => handleChange('loadingPortId', v)}
          placeholder="port_xxx"
        />
        <TextField
          label={t.testForm.destinationPortId}
          value={(data.destinationPortId as string) || ''}
          onChange={(v) => handleChange('destinationPortId', v)}
          placeholder="port_yyy"
        />
      </div>
      <TextField
        label={t.testForm.purchaseOrderId}
        value={(data.purchaseOrderId as string) || ''}
        onChange={(v) => handleChange('purchaseOrderId', v)}
        placeholder="po_xxx"
      />
    </div>
  );
};

const LoadFields: React.FC<{
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}> = ({ data, onChange }) => {
  const t = useTranslations();
  const handleChange = (field: string, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="@LoadFields space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.loadId}
          value={(data.id as string) || ''}
          onChange={(v) => handleChange('id', v)}
          placeholder="load_test123"
        />
        <TextField
          label={t.testForm.documentNumber}
          value={(data.documentNumber as string) || ''}
          onChange={(v) => handleChange('documentNumber', v)}
          placeholder="LD-2024-001"
        />
      </div>
      <Select<string>
        label={t.form.status}
        value={(data.status as string) || 'PENDING'}
        onChange={(v: string) => handleChange('status', v)}
        options={LOAD_STATUSES.map((s) => ({ key: s, value: s, label: s }))}
      />
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.containerNumber}
          value={(data.containerNumber as string) || ''}
          onChange={(v) => handleChange('containerNumber', v)}
          placeholder="MSKU1234567"
        />
        <TextField
          label={t.testForm.sealNumber}
          value={(data.sealNumber as string) || ''}
          onChange={(v) => handleChange('sealNumber', v)}
          placeholder="SEAL123"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <NumberField
          label={t.testForm.heavyWeight}
          value={(data.heavyWeight as number) || 0}
          onChange={(v) => handleChange('heavyWeight', v)}
        />
        <NumberField
          label={t.testForm.lightWeight}
          value={(data.lightWeight as number) || 0}
          onChange={(v) => handleChange('lightWeight', v)}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.salesOrderId}
          value={(data.salesOrderId as string) || ''}
          onChange={(v) => handleChange('salesOrderId', v)}
          placeholder="so_xxx"
        />
        <TextField
          label={t.testForm.bookingIdRef}
          value={(data.bookingId as string) || ''}
          onChange={(v) => handleChange('bookingId', v)}
          placeholder="bkng_xxx"
        />
      </div>
      <TextField
        label={t.testForm.facilityId}
        value={(data.facilityId as string) || ''}
        onChange={(v) => handleChange('facilityId', v)}
        placeholder="fac_xxx"
      />
    </div>
  );
};

const SalesOrderFields: React.FC<{
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}> = ({ data, onChange }) => {
  const t = useTranslations();
  const handleChange = (field: string, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="@SalesOrderFields space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.salesOrderId}
          value={(data.id as string) || ''}
          onChange={(v) => handleChange('id', v)}
          placeholder="so_test123"
        />
        <TextField
          label={t.testForm.documentNumber}
          value={(data.documentNumber as string) || ''}
          onChange={(v) => handleChange('documentNumber', v)}
          placeholder="SO-2024-001"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Select<string>
          label={t.form.status}
          value={(data.status as string) || 'DRAFT'}
          onChange={(v: string) => handleChange('status', v)}
          options={SALES_ORDER_STATUSES.map((s) => ({
            key: s,
            value: s,
            label: s,
          }))}
        />
        <Select<string>
          label={t.editor.config.fields.type}
          value={(data.type as string) || 'DOMESTIC'}
          onChange={(v: string) => handleChange('type', v)}
          options={SALES_ORDER_TYPES.map((s) => ({
            key: s,
            value: s,
            label: s,
          }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.facilityId}
          value={(data.facilityId as string) || ''}
          onChange={(v) => handleChange('facilityId', v)}
          placeholder="fac_xxx"
        />
        <TextField
          label={t.testForm.accountId}
          value={(data.accountId as string) || ''}
          onChange={(v) => handleChange('accountId', v)}
          placeholder="acct_xxx"
        />
      </div>
      <TextField
        label={t.testForm.paymentCurrency}
        value={(data.paymentCurrency as string) || ''}
        onChange={(v) => handleChange('paymentCurrency', v)}
        placeholder="USD"
      />
      <TextArea
        label={t.testForm.notes}
        value={(data.notes as string) || ''}
        onChange={(v) => handleChange('notes', v)}
        placeholder="..."
        rows={2}
      />
    </div>
  );
};

const InventoryFields: React.FC<{
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}> = ({ data, onChange }) => {
  const t = useTranslations();
  const handleChange = (field: string, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="@InventoryFields space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.itemId}
          value={(data.id as string) || ''}
          onChange={(v) => handleChange('id', v)}
          placeholder="item_test123"
        />
        <TextField
          label={t.testForm.skuNumber}
          value={(data.skuNumber as string) || ''}
          onChange={(v) => handleChange('skuNumber', v)}
          placeholder="SKU-001"
        />
      </div>
      <TextField
        label={t.testForm.itemName}
        value={(data.name as string) || ''}
        onChange={(v) => handleChange('name', v)}
        placeholder="..."
      />
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.categoryId}
          value={(data.categoryId as string) || ''}
          onChange={(v) => handleChange('categoryId', v)}
          placeholder="cat_xxx"
        />
        <TextField
          label={t.testForm.itemGroupId}
          value={(data.itemGroupId as string) || ''}
          onChange={(v) => handleChange('itemGroupId', v)}
          placeholder="grp_xxx"
        />
      </div>
      <TextArea
        label={t.testForm.itemDescription}
        value={(data.description as string) || ''}
        onChange={(v) => handleChange('description', v)}
        placeholder="..."
        rows={2}
      />
    </div>
  );
};

const InvoiceFields: React.FC<{
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}> = ({ data, onChange }) => {
  const t = useTranslations();
  const handleChange = (field: string, value: unknown) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="@InvoiceFields space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.invoiceId}
          value={(data.id as string) || ''}
          onChange={(v) => handleChange('id', v)}
          placeholder="inv_test123"
        />
        <TextField
          label={t.testForm.documentNumber}
          value={(data.documentNumber as string) || ''}
          onChange={(v) => handleChange('documentNumber', v)}
          placeholder="INV-2024-001"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Select<string>
          label={t.form.status}
          value={(data.status as string) || 'PENDING'}
          onChange={(v: string) => handleChange('status', v)}
          options={INVOICE_STATUSES.map((s) => ({
            key: s,
            value: s,
            label: s,
          }))}
        />
        <Select<string>
          label={t.editor.config.fields.type}
          value={(data.type as string) || 'INBOUND'}
          onChange={(v: string) => handleChange('type', v)}
          options={INVOICE_TYPES.map((s) => ({ key: s, value: s, label: s }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <NumberField
          label={t.testForm.invoiceAmount}
          value={(data.amount as number) || 0}
          onChange={(v) => handleChange('amount', v)}
          formatOptions={{ style: 'currency', currency: 'USD' }}
        />
        <TextField
          label={t.testForm.dueDate}
          value={(data.dueDate as string) || ''}
          onChange={(v) => handleChange('dueDate', v)}
          placeholder="2024-12-31"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.testForm.facilityId}
          value={(data.facilityId as string) || ''}
          onChange={(v) => handleChange('facilityId', v)}
          placeholder="fac_xxx"
        />
        <TextField
          label={t.testForm.accountId}
          value={(data.accountId as string) || ''}
          onChange={(v) => handleChange('accountId', v)}
          placeholder="acct_xxx"
        />
      </div>
      <TextArea
        label={t.testForm.notes}
        value={(data.notes as string) || ''}
        onChange={(v) => handleChange('notes', v)}
        placeholder="..."
        rows={2}
      />
    </div>
  );
};

export const BusinessEventTestForm: React.FC<BusinessEventTestFormProps> = ({
  value,
  onChange,
  configuredEntityType,
  configuredEventCase,
}) => {
  const t = useTranslations();
  const effectiveEntityType = value.entityType || configuredEntityType || '';
  const effectiveEventCase = value.eventCase || configuredEventCase || '';
  const isConfigured = !!(configuredEntityType && configuredEventCase);

  const entities = React.useMemo(() => getEntities(), []);
  const eventCases = React.useMemo(
    () =>
      effectiveEntityType ? getEventCasesForEntity(effectiveEntityType) : [],
    [effectiveEntityType],
  );

  const handleEntityTypeChange = React.useCallback(
    (entityType: string) => {
      onChange({ entityType, eventCase: '', data: {} });
    },
    [onChange],
  );

  const handleEventCaseChange = React.useCallback(
    (eventCase: string) => {
      onChange({ ...value, eventCase });
    },
    [value, onChange],
  );

  const handleDataChange = React.useCallback(
    (data: Record<string, unknown>) => {
      onChange({ ...value, data });
    },
    [value, onChange],
  );

  const renderEntityForm = () => {
    switch (effectiveEntityType) {
      case 'booking':
        return <BookingFields data={value.data} onChange={handleDataChange} />;
      case 'load':
        return <LoadFields data={value.data} onChange={handleDataChange} />;
      case 'salesOrder':
        return (
          <SalesOrderFields data={value.data} onChange={handleDataChange} />
        );
      case 'item':
        return (
          <InventoryFields data={value.data} onChange={handleDataChange} />
        );
      case 'invoice':
        return <InvoiceFields data={value.data} onChange={handleDataChange} />;
      default:
        return (
          <p className="text-sm text-neutral/60">
            {t.dialogs.test.businessEvent.selectEntity}
          </p>
        );
    }
  };

  const entityLabel =
    entities.find((e) => e.key === effectiveEntityType)?.label ||
    effectiveEntityType;

  return (
    <div className="@BusinessEventTestForm space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Select<string>
          label={t.dialogs.test.businessEvent.entity}
          value={effectiveEntityType}
          onChange={handleEntityTypeChange}
          options={entities.map((e) => ({
            key: e.key,
            value: e.key,
            label: e.label,
          }))}
          isDisabled={isConfigured}
          description={
            isConfigured
              ? t.dialogs.test.businessEvent.entityConfigured
              : t.dialogs.test.businessEvent.entityDescription
          }
        />
        <Select<string>
          label={t.dialogs.test.businessEvent.event}
          value={effectiveEventCase}
          onChange={handleEventCaseChange}
          options={eventCases.map((ec) => ({
            key: ec.key,
            value: ec.key,
            label: ec.label,
          }))}
          isDisabled={isConfigured || !effectiveEntityType}
          description={
            isConfigured
              ? t.dialogs.test.businessEvent.eventConfigured
              : t.dialogs.test.businessEvent.eventDescription
          }
        />
      </div>

      {effectiveEntityType && (
        <div className="rounded-lg border bg-neutral/5 p-4">
          <h4 className="mb-4 text-sm font-medium">
            {t.dialogs.test.businessEvent.dataTitle(entityLabel)}
          </h4>
          {renderEntityForm()}
        </div>
      )}
    </div>
  );
};

export const createDefaultBusinessEventTestPayload = (
  configuredEntityType?: string,
  configuredEventCase?: string,
): BusinessEventTestPayload => ({
  entityType: configuredEntityType || '',
  eventCase: configuredEventCase || '',
  data: {},
});
